/**
 * Cosa cambia fra due revisioni: righe del compose e variabili dell'environment.
 */

export type Riga = { readonly tipo: "uguale" | "aggiunta" | "tolta"; readonly testo: string };
export type Blocco = Riga | { readonly tipo: "invariate"; readonly conta: number };

// Il ritorno a capo finale non è una riga: mostrarlo darebbe una riga vuota in fondo.
const righeDi = (testo: string) => (testo === "" ? [] : testo.replace(/\n$/, "").split("\n"));

/**
 * Differenza riga per riga, sulla sottosequenza comune più lunga.
 *
 * ponytail: tabella O(n·m), qualche centinaio di righe per compose bastano;
 * Myers se un giorno i compose diventano di migliaia di righe.
 */
export function differenzaRighe(prima: string, dopo: string): Riga[] {
  const a = righeDi(prima);
  const b = righeDi(dopo);

  // comune[i][j]: lunghezza della sottosequenza comune fra a[i..] e b[j..].
  const comune = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      comune[i]![j] = a[i] === b[j] ? comune[i + 1]![j + 1]! + 1 : Math.max(comune[i + 1]![j]!, comune[i]![j + 1]!);
    }
  }

  const righe: Riga[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      righe.push({ tipo: "uguale", testo: a[i++]! });
      j++;
    } else if (j >= b.length || (i < a.length && comune[i + 1]![j]! >= comune[i]![j + 1]!)) {
      // A parità, prima la tolta: una riga cambiata si legge «vecchia, poi nuova».
      righe.push({ tipo: "tolta", testo: a[i++]! });
    } else {
      righe.push({ tipo: "aggiunta", testo: b[j++]! });
    }
  }
  return righe;
}

/** Tiene `contesto` righe invariate attorno a ogni modifica; il resto diventa un conteggio. */
export function comprimi(righe: readonly Riga[], contesto = 3): Blocco[] {
  const vicine = righe.map((_, i) =>
    righe.slice(Math.max(0, i - contesto), i + contesto + 1).some((r) => r.tipo !== "uguale"),
  );

  const blocchi: Blocco[] = [];
  righe.forEach((riga, i) => {
    if (vicine[i]) return void blocchi.push(riga);
    const ultimo = blocchi.at(-1);
    if (ultimo?.tipo === "invariate") blocchi[blocchi.length - 1] = { tipo: "invariate", conta: ultimo.conta + 1 };
    else blocchi.push({ tipo: "invariate", conta: 1 });
  });
  return blocchi;
}

export type Variabile =
  | { readonly nome: string; readonly tipo: "aggiunta"; readonly dopo: string }
  | { readonly nome: string; readonly tipo: "tolta"; readonly prima: string }
  | { readonly nome: string; readonly tipo: "cambiata"; readonly prima: string; readonly dopo: string };

/** Variabili aggiunte, tolte o cambiate, in ordine di nome. */
export function differenzaVariabili(
  prima: Readonly<Record<string, string>>,
  dopo: Readonly<Record<string, string>>,
): Variabile[] {
  return [...new Set([...Object.keys(prima), ...Object.keys(dopo)])].sort().flatMap((nome): Variabile[] => {
    const p = prima[nome];
    const d = dopo[nome];
    if (p === undefined) return [{ nome, tipo: "aggiunta", dopo: d! }];
    if (d === undefined) return [{ nome, tipo: "tolta", prima: p }];
    return p === d ? [] : [{ nome, tipo: "cambiata", prima: p, dopo: d }];
  });
}
